import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';

// layout 
import AppLayout from '../../../layouts/AppLayout/AppLayout';

// components 
import { NioSection, NioField, NioBadge, NioButton, NioSubscribeField } from '../../../components';
import { useTranslation } from '../../../i18n/I18nProvider';
import { translateServiceLabel } from '../../../i18n/translateService';

// utils
import api from '../../../utils/api';

function getLoggedInUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function isProviderForService(user, service) {
  if (!user?.serviceId) return false;
  const sid = (service._id && typeof service._id === 'object' && service._id.toString) ? service._id.toString() : (service._id || '');
  return user.serviceId === sid || user.serviceId === service.title;
}

function ServicesPage() {
  const { t, language } = useTranslation();
  const [allServices, setAllServices] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [loggedInUser, setLoggedInUser] = useState(getLoggedInUser);

  useEffect(() => {
    setLoggedInUser(getLoggedInUser());
  }, []);

  // Service groups mapping
  const serviceGroups = {
    'all': 'All Services',
    'Digital & Professional': ['Digital', 'Professional'],
    'Personal & Family': ['Personal', 'Family'],
    'Home Services': ['Home', 'Service', 'Maintenance', 'Repair'],
    'Moving & Delivery': ['Moving', 'Delivery'],
    'Gardening & Outdoor': ['Gardening', 'Outdoor']
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        // Auto-init non-destructif : ajoute seulement les services manquants (dédup = admin)
        try {
          await api.initializeServices();
        } catch (error) {
          console.log('Services init:', error.message || 'skipped');
        }
        // Initialize services if needed
        await api.initializeServices(false);
        // Fetch all services
        const response = await api.getServices({});
        
        if (response.success) {
          // Ensure image paths start with / for public folder
          const servicesWithFixedPaths = response.data.map(service => ({
            ...service,
            cover: service.cover?.startsWith('/') ? service.cover : `/${service.cover}`,
            user: service.user ? {
              ...service.user,
              cover: service.user.cover?.startsWith('/') ? service.user.cover : `/${service.user.cover}`
            } : service.user
          }));
          setAllServices(servicesWithFixedPaths);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  // Filter and sort services
  useEffect(() => {
    let filtered = [...allServices];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(service => 
        service.title?.toLowerCase().includes(query) ||
        service.description?.toLowerCase().includes(query) ||
        (Array.isArray(service.tags) ? service.tags.some(tag => tag.toLowerCase().includes(query)) : false)
      );
    }

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(service => {
        const tags = Array.isArray(service.tags) ? service.tags : [service.tags];
        return tags.some(tag => tag === selectedCategory);
      });
    }

    // Apply service group filter
    if (selectedGroup !== 'all') {
      const groupTags = serviceGroups[selectedGroup] || [];
      filtered = filtered.filter(service => {
        const tags = Array.isArray(service.tags) ? service.tags : [service.tags];
        return tags.some(tag => groupTags.includes(tag));
      });
    }

    // Apply sorting
    if (sortBy === 'name-asc') {
      filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } else if (sortBy === 'name-desc') {
      filtered.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
    } else if (sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    } else {
      filtered.sort((a, b) => {
        const priorityDiff = getServicePriority(a) - getServicePriority(b);
        if (priorityDiff !== 0) return priorityDiff;
        return (a.title || '').localeCompare(b.title || '');
      });
    }

    setServices(filtered);
  }, [allServices, searchQuery, selectedCategory, selectedGroup, sortBy]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleGroupChange = (e) => {
    setSelectedGroup(e.target.value);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  const featuredServices = services.slice(0, 2);
  const otherServices = services.slice(2);
  const isMechanicService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('mecancien') || t.includes('mécancien') || t.includes('mecanicien') || t.includes('mécanicien');
  };
  const isSecurityService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('sécurité') || t.includes('securite') || t.includes('serrurerie');
  };
  const isMovingService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('déménagement') || t.includes('demenagement') || t.includes('transport');
  };
  const isCleaningService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('ménage') || t.includes('menage') || t.includes('nettoyage') || t.includes('netoyage');
  };
  const isGardeningService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('jardinage') || t.includes('jardin') || t.includes('extérieur') || t.includes('exterieur');
  };
  const isCarpentryService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('menuiserie') || t.includes('bois');
  };
  const isElectricityService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('électricité') || t.includes('electricite') || t.includes('électrique') || t.includes('electrique');
  };
  const isPlumbingService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('plomberie') || t.includes('chauffage');
  };
  const isFloorWallService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('sol') || t.includes('mur');
  };
  const isBathroomService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('salle de bain') || t.includes('salledebain') || t.includes('bain');
  };
  const isKitchenService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('cuisine');
  };
  const isFurnitureDecorationService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('meuble') || t.includes('décoration') || t.includes('decoration');
  };
  const getServicePriority = (service) => {
    if (isFloorWallService(service)) return 0;
    if (isFurnitureDecorationService(service)) return 1;
    if (isKitchenService(service)) return 2;
    if (isBathroomService(service)) return 3;
    if (isPlumbingService(service)) return 4;
    if (isCarpentryService(service)) return 5;
    if (isGardeningService(service)) return 6;
    if (isElectricityService(service)) return 7;
    if (isMovingService(service)) return 8;
    return 99;
  };
  const getServiceDestination = (service, serviceId) => {
    if (isMechanicService(service)) return '/services/mecancien';
    if (isSecurityService(service)) return '/services/securite-serrurerie';
    if (isMovingService(service)) return '/services/demenagement';
    if (isCleaningService(service)) return '/services/menage-nettoyage';
    if (isGardeningService(service)) return '/services/jardinage';
    if (isCarpentryService(service)) return '/services/menuiserie';
    if (isElectricityService(service)) return '/services/electricite';
    if (isPlumbingService(service)) return '/services/plomberie-chauffage';
    if (isFloorWallService(service)) return '/services/sol-mur';
    if (isBathroomService(service)) return '/services/salle-de-bain';
    if (isKitchenService(service)) return '/services/cuisine';
    if (isFurnitureDecorationService(service)) return '/services/meuble-decoration';
    return `/service/${serviceId}/providers`;
  };

  const getServiceCover = (service) => {
    if (isMechanicService(service)) return '/images/mecanique-aa.png';
    const cover = service.cover || '/images/blog/a.jpg';
    return cover.startsWith('/') ? cover : `/${cover}`;
  };
  const getServiceDisplayTitle = (service) => {
    if (service?.titles?.[language]) return service.titles[language];
    if (isMovingService(service)) return translateServiceLabel(t, 'Déménagement');
    return translateServiceLabel(t, service?.title);
  };

  const renderServiceCard = (service, idx, { featured = false, featuredCount = 0 } = {}) => {
    const serviceId = service._id || encodeURIComponent(service.title);
    const isMyService = isProviderForService(loggedInUser, service);
    const to = getServiceDestination(service, serviceId);
    const coverUrl = getServiceCover(service);
    const colProps = featured
      ? { xs: 12, md: featuredCount === 1 ? 12 : 6 }
      : { xs: 6, md: 6, lg: 3 };

    return (
      <Col {...colProps} key={service._id || `${featured ? 'f' : 'o'}-${idx}`}>
        <Link
          to={to}
          className={`services-grid-tile ${featured ? 'services-grid-tile--large' : ''} ${isMyService ? 'border border-primary border-2' : ''}`}
        >
          <div className="services-grid-tile__bg" style={{ backgroundImage: `url(${coverUrl})` }} />
          <div className="services-grid-tile__overlay" aria-hidden />
          {isMyService && (
            <NioBadge className={`services-grid-tile__badge ${featured ? 'm-3' : 'm-2'} text-bg-primary`}>
              {t('common.yourService')}
            </NioBadge>
          )}
          <span className="services-grid-tile__title">{getServiceDisplayTitle(service)}</span>
        </Link>
      </Col>
    );
  };

  return (
    <AppLayout title={t('servicesPage.pageTitle')} rootClass="layout-1">
      <style>{`
        .services-filter-wrap {
          background: #f8fbff;
          border: 1px solid #e9eff6;
          border-radius: 16px;
          padding: 1.25rem;
        }
        @media (min-width: 768px) {
          .services-filter-wrap {
            padding: 1.5rem;
          }
        }
        .services-grid-tile {
          position: relative;
          display: block;
          overflow: hidden;
          border-radius: 18px;
          min-height: 200px;
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
          transition: box-shadow 0.3s ease, transform 0.3s ease, border-color 0.3s ease;
        }
        .services-grid-tile:hover {
          box-shadow: 0 14px 36px rgba(15, 23, 42, 0.2);
          transform: translateY(-3px);
        }
        .services-grid-tile--large {
          min-height: 260px;
        }
        @media (min-width: 992px) {
          .services-grid-tile--large {
            min-height: 300px;
          }
        }
        .services-grid-tile__bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          transition: transform 0.45s ease;
        }
        .services-grid-tile:hover .services-grid-tile__bg {
          transform: scale(1.06);
        }
        .services-grid-tile__overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            180deg,
            rgba(15, 23, 42, 0.25) 0%,
            rgba(15, 23, 42, 0.55) 100%
          );
        }
        .services-grid-tile__badge {
          position: absolute;
          top: 0;
          right: 0;
          z-index: 2;
        }
        .services-grid-tile__title {
          position: absolute;
          inset: 0;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          margin: 0;
          color: #fff;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          font-weight: 700;
          text-align: center;
          text-shadow: 0 2px 14px rgba(0, 0, 0, 0.85);
          font-size: clamp(0.75rem, 1.5vw, 0.95rem);
          line-height: 1.3;
        }
        .services-grid-tile--large .services-grid-tile__title {
          font-size: clamp(0.9rem, 2vw, 1.2rem);
          letter-spacing: 0.08em;
        }
      `}</style>

      {/*  Resource Section Start  */}
      <NioSection className="overflow-hidden pt-120 pt-lg-160" masks={["blur-1 left center"]}>
        <NioSection.Content>
          <Row className="justify-content-center text-center">
            <Col lg={8} xl={6}>
              <div className="nk-section-head">
                <span className="d-inline-block fs-14 text-uppercase text-primary fw-semibold mb-2" >{t('servicesPage.badge')}</span>
                <h2 >{t('servicesPage.title')}</h2>
                <p className="fs-20" > {t('servicesPage.subtitle')} </p>
                <div className="mt-4 d-flex gap-3 justify-content-center flex-wrap">
                  <NioButton 
                    href="/post-request"
                    className="btn-lg btn-primary"
                    label={t('servicesPage.becomeProvider')}
                    icon="plus after"
                  />
                  <NioButton 
                    href="/user-request"
                    className="btn-lg btn-outline-primary"
                    label={t('servicesPage.publishRequest')}
                    icon="search after"
                  />
                </div>
              </div>
            </Col>
            <Col lg={10} xl={12}>
              <div className="nk-filter-wrap pb-5 pb-md-7 services-filter-wrap">
                {/* Search Bar */}
                <div className="mb-4">
                  <NioField.Input 
                    icon="search before z-1" 
                    placeholder={t('home.searchPlaceholder')} 
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </div>

                {/* Filter Controls Row */}
                <Row className="g-3 mb-4">
                  <Col md={6} lg={4}>
                    <div className="form-group">
                      <label className="form-label fw-semibold mb-2">{t('home.groupLabel')}</label>
                      <select 
                        className="form-control form-control-lg"
                        value={selectedGroup}
                        onChange={handleGroupChange}
                        style={{ cursor: 'pointer' }}
                      >
                        {Object.keys(serviceGroups).map(group => (
                          <option key={group} value={group}>
                            {group === 'all' ? t('home.groups.all') : group}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Col>
                  <Col md={6} lg={4}>
                    <div className="form-group">
                      <label className="form-label fw-semibold mb-2">{t('home.sortLabel')}</label>
                      <select 
                        className="form-control form-control-lg"
                        value={sortBy}
                        onChange={handleSortChange}
                        style={{ cursor: 'pointer' }}
                      >
                        <option value="default">{t('home.sortDefault')}</option>
                        <option value="name-asc">{t('home.sortNameAsc')}</option>
                        <option value="name-desc">{t('home.sortNameDesc')}</option>
                        <option value="newest">{t('home.sortNewest')}</option>
                      </select>
                    </div>
                  </Col>
                  <Col md={12} lg={4}>
                    <div className="form-group">
                      <label className="form-label fw-semibold mb-2">{t('home.results', { count: services.length })}</label>
                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => {
                            setSearchQuery('');
                            setSelectedCategory('all');
                            setSelectedGroup('all');
                            setSortBy('default');
                          }}
                        >
                          {t('home.clearFilters')}
                        </button>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
          <Row>
            <Col xl={12}>
              {loading ? (
                <div className="text-center py-5">
                  <p className="fs-18">{t('home.loadingServices')}</p>
                </div>
              ) : services.length > 0 ? (
                <>
                  {featuredServices.length > 0 && (
                    <Row className="g-3 g-lg-4 mb-3 mb-lg-4">
                      {featuredServices.map((service, idx) => renderServiceCard(service, idx, { featured: true, featuredCount: featuredServices.length }))}
                    </Row>
                  )}
                  {otherServices.length > 0 && (
                    <Row className="g-3 g-lg-4">
                      {otherServices.map((service, idx) => renderServiceCard(service, idx))}
                    </Row>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <p className="fs-18">{t('home.noServices')}</p>
                </div>
              )}
            </Col>
          </Row>
        </NioSection.Content>
      </NioSection>
      {/*  Resource Section End  */}
              
      {/*  Newsletter Section End  */}
    </AppLayout>
  )
}

export default ServicesPage;
